import React from 'react';
import tw, { styled } from 'twin.macro';

import { brandSettingsHref } from 'views/settings/brands/brandLink';
import { useLoopingClock } from 'hooks/useLoopingClock';
import { VideoTemplateStyle } from './types';
import { TEMPLATE_STYLES } from './mockData';
import { Track1PreviewFrame } from './Track1PreviewFrame';
import { loopedTime, planForStyle, StyleExamplesState } from './styleExamples';
import { planAspect } from './previewTimeline';
import { PreviewPlan } from 'api/video';

interface TemplateStylePickerProps {
  selected: VideoTemplateStyle;
  onSelect: (style: VideoTemplateStyle) => void;
  /**
   * SYM-6226. The three moving examples, as the server drew them. Fetched once
   * per draft by the provider and handed down; see useStyleExamples.ts.
   */
  examples?: StyleExamplesState;
  /** Asks for them again after a failure. */
  onRetryExamples?: () => void;
  /**
   * SYM-6353. Small cards: a thumbnail and the look's name, with what the look
   * does on hover. Bring to Life shows the chosen look large beside its
   * choices, so the three cards there only have to be told apart. Track 1's
   * Style step has the pop-up to itself and keeps the large cards.
   */
  compact?: boolean;
  /**
   * SYM-6353. The looks are drawn over a clip that already moves (Bring to
   * Life), so each is described without Track 1's photo drift.
   */
  onAClip?: boolean;
  /** The heading over the cards; null when the caller draws its own. */
  label?: string | null;
}

const Container = styled.div<{ $compact: boolean }>`
  ${({ $compact }) => ($compact ? tw`mb-0` : tw`mb-6`)}
`;

const Label = tw.p`text-sm font-medium text-gray-500 mb-3`;

const Grid = tw.div`grid grid-cols-3 gap-3`;

const StyleCard = styled.button<{ isSelected: boolean; accentColor: string }>`
  ${tw`relative flex flex-col rounded-lg border-2 overflow-hidden transition-all duration-200 text-left hover:shadow-md`}
  ${({ isSelected }) =>
    isSelected
      ? tw`ring-[3px] ring-indigo-500 border-transparent`
      : tw`border-gray-200 hover:border-gray-300`}
`;

/**
 * SYM-6226, rebuilt 22 September 2026. Each look is shown as a short MOVING
 * example on the customer's own picture, colour and logo, instead of a clip we
 * shipped in the app.
 *
 * WHY THE CLIPS HAD TO GO. Three files in `public/` could only ever show one
 * stock photograph in sympl's own colour, so the one thing a customer is
 * choosing between -- what this look does to THEIR video -- was the one thing
 * they could not see. The files are deleted, not left beside this: a
 * placeholder in the tree is a placeholder somebody ships.
 *
 * NOTHING HERE IS LAID OUT IN THE BROWSER. Each example is a preview plan, drawn
 * by the same code that composites the finished video, played by the same
 * component the Preview step uses (Track1PreviewFrame). So a line that breaks
 * in the example breaks there in the render, and there is no second code path
 * for the two to differ by. That is SYM-6179's lesson.
 *
 * THE WHOLE FRAME, NOT A STRIP (Steven, 23 Sep 2026). The first version kept
 * the old clip's wide, short box and showed only the band where the text sits.
 * The machine run of round 8 showed what that costs: the logo never appeared
 * and the subtext was cut off at the bottom, so two of the four things a look
 * is chosen on were not on screen. The example is now the finished video's
 * whole frame, in the video's own shape.
 */
const ExampleStrip = tw.div`relative w-full bg-gray-100 pointer-events-none`;

/**
 * The shape the box takes before the answer is here: the feed frame (1080 by
 * 1350), which is what a Track 1 video is drawn in today. Once the examples
 * arrive, each box takes its plan's own shape instead.
 */
const SHAPE_BEFORE_ANSWER = 4 / 5;

/**
 * The tallest a frame may be, as a share of the window's height. A 9:16 frame
 * the full width of a card would push the card names below the fold on a
 * laptop; past this height the frame gets narrower instead of taller.
 */
const MAX_FRAME_HEIGHT_VH = 42;

/**
 * The whole example, width over height = `$aspect`.
 *
 * Its height comes from a percentage of its own width rather than from
 * `aspect-ratio`, because the player inside is positioned against this box:
 * a browser that did not understand the property would leave this box no
 * height at all and the card would draw nothing. The percentage sits on a
 * pseudo-element, not on the box: a padding percentage is taken from the
 * PARENT's width, and the box's own width is capped (MAX_FRAME_HEIGHT_VH), so
 * on the box itself it would give the height of an uncapped frame.
 */
const ExampleStage = styled.div<{ $aspect: number; $maxHeight: string }>`
  ${tw`relative mx-auto overflow-hidden`}
  width: min(100%, calc(${({ $maxHeight }) => $maxHeight} * ${({ $aspect }) =>
    $aspect}));

  &::before {
    content: '';
    display: block;
    padding-bottom: ${({ $aspect }) => 100 / $aspect}%;
  }
`;

/**
 * SYM-6226. What the three boxes show before the answer is here, and if it
 * never comes.
 *
 * ALL OR NOTHING, BY CONSTRUCTION. The server answers all three looks in one
 * call so that the three examples agree on the picture, which means there is no
 * state where one box is ready and another is not. Nothing here has to decide
 * what two thirds of an answer looks like.
 *
 * WHILE IT IS ON ITS WAY: the same quiet grey block in all three boxes, gently
 * pulsing, and the cards stay pressable. No spinner, for two reasons. Three
 * spinners say three things are happening when one is. And a customer who
 * already knows which look they want must not be made to wait on pictures they
 * are not going to read -- the look is chosen from the card, and the card is
 * whole: its name, its description and its colour are all there.
 *
 * IF IT FAILS: the same block, no longer pulsing, because a pulse promises
 * something is still coming. The reason is said ONCE above the row rather than
 * three times inside it -- one call, one failure, one sentence -- with a way to
 * ask again. The step then falls back to exactly what it was before this
 * ticket: three names and three descriptions, which is worse and is still a
 * working step.
 */
const ExamplePlaceholder = styled.div<{ $pulsing: boolean }>`
  ${tw`absolute inset-0 bg-gray-200`}
  ${({ $pulsing }) => $pulsing && tw`animate-pulse`}
`;

const ExamplesFailed = tw.p`mb-3 text-xs text-amber-600`;

const RetryLink = tw.button`text-indigo-600 font-medium hover:underline cursor-pointer bg-transparent border-0 p-0`;

/**
 * SYM-6226 (Steven, 22 Sep 2026). Said whenever the colour on screen is ours
 * rather than theirs.
 *
 * The examples are drawn in navy when a brand holds no colour of its own,
 * because three colourless examples would make Dynamic and Professional look
 * like Minimal and the customer would be choosing between three of the same
 * thing. The price of showing a colour they did not choose is that they might
 * take it for theirs, so this says outright that they have not set one, and
 * links to where it is set.
 */
const NoBrandColour = tw.p`mt-3 text-xs text-gray-500`;

const StyleContent = tw.div`p-3`;

const StyleName = tw.p`text-sm font-semibold text-gray-800`;

const StyleDescription = tw.p`text-xs text-gray-500 mt-0.5 leading-tight`;

const AccentBar = styled.div<{ color: string }>`
  ${tw`h-1 w-full`}
  background-color: ${({ color }) => color};
`;

/**
 * SYM-6353. One look's moving example, or the grey block while it is on its
 * way (pulsing) or if it never came (still). The ONE place a look's example is
 * drawn: the three cards here use it, and so does Bring to Life's large
 * preview, so the two pop-ups cannot come to show a look differently.
 *
 * `maxHeight` is a CSS length: past it, the frame gets narrower rather than
 * taller.
 */
export const LookFrame: React.FC<{
  plan: PreviewPlan | null;
  clock: number;
  loading: boolean;
  maxHeight?: string;
}> = ({ plan, clock, loading, maxHeight = `${MAX_FRAME_HEIGHT_VH}vh` }) =>
  plan ? (
    <ExampleStage $aspect={planAspect(plan)} $maxHeight={maxHeight}>
      {/*
        Silent on purpose: `isPlaying` is what drives the plan's music, and
        looping examples singing over each other on a screen nobody pressed
        play on is not what the clips did. The frame draws from the clock
        either way.
      */}
      <Track1PreviewFrame
        plan={plan}
        currentTime={loopedTime(clock, plan.duration)}
        isPlaying={false}
      />
    </ExampleStage>
  ) : (
    <ExampleStage $aspect={SHAPE_BEFORE_ANSWER} $maxHeight={maxHeight}>
      <ExamplePlaceholder
        data-testid="style-example-placeholder"
        $pulsing={loading}
      />
    </ExampleStage>
  );

/** SYM-6353. The small card: thumbnail left, name right. */
const CompactGrid = tw.div`grid grid-cols-3 gap-2`;

const CompactCard = styled.button<{ isSelected: boolean }>`
  ${tw`grid items-center gap-2 rounded-lg border bg-white p-1.5 text-left transition-all duration-200 hover:shadow-md`}
  grid-template-columns: 52px minmax(0, 1fr);
  ${({ isSelected }) =>
    isSelected
      ? tw`ring-2 ring-indigo-500 border-indigo-500`
      : tw`border-gray-200 hover:border-gray-300`}
`;

const CompactThumb = tw.div`relative w-[52px] overflow-hidden rounded pointer-events-none bg-gray-100`;

/** The tallest a small card's thumbnail is: a 4:5 frame 52px wide. */
const COMPACT_THUMB_HEIGHT = '65px';

const TemplateStylePicker: React.FC<TemplateStylePickerProps> = ({
  selected,
  onSelect,
  examples = { status: 'loading' },
  onRetryExamples,
  compact = false,
  onAClip = false,
  label = 'Video Style',
}) => {
  const ready = examples.status === 'ready';

  // One clock for all three, and only while there is something for it to move.
  const clock = useLoopingClock(ready);

  return (
    <Container $compact={compact}>
      {label !== null && <Label>{label}</Label>}

      {examples.status === 'failed' && (
        <ExamplesFailed data-testid="style-examples-failed">
          The examples of these looks could not be loaded.
          {compact ? '' : ' What each one does is described below.'}{' '}
          {onRetryExamples && (
            <RetryLink type="button" onClick={onRetryExamples}>
              Try again
            </RetryLink>
          )}
        </ExamplesFailed>
      )}

      {compact ? (
        <CompactGrid>
          {TEMPLATE_STYLES.map((style) => (
            <CompactCard
              key={style.id}
              type="button"
              isSelected={selected === style.id}
              aria-pressed={selected === style.id}
              title={onAClip ? style.onAClip : style.description}
              onClick={() => onSelect(style.id)}
            >
              <CompactThumb
                data-testid="style-example"
                data-style={style.id}
                aria-label={`Example of the ${style.name} look`}
              >
                <LookFrame
                  plan={
                    ready ? planForStyle(examples.examples, style.id) : null
                  }
                  clock={clock}
                  loading={examples.status === 'loading'}
                  maxHeight={COMPACT_THUMB_HEIGHT}
                />
              </CompactThumb>
              <StyleName>{style.name}</StyleName>
            </CompactCard>
          ))}
        </CompactGrid>
      ) : (
        <Grid>
          {TEMPLATE_STYLES.map((style) => (
            <StyleCard
              key={style.id}
              type="button"
              isSelected={selected === style.id}
              accentColor={style.color}
              onClick={() => onSelect(style.id)}
            >
              <ExampleStrip
                data-testid="style-example"
                data-style={style.id}
                aria-label={`Example of the ${style.name} look`}
              >
                <LookFrame
                  plan={
                    ready ? planForStyle(examples.examples, style.id) : null
                  }
                  clock={clock}
                  loading={examples.status === 'loading'}
                />
              </ExampleStrip>
              <AccentBar color={style.color} />
              <StyleContent>
                <StyleName>{style.name}</StyleName>
                <StyleDescription>{style.description}</StyleDescription>
              </StyleContent>
            </StyleCard>
          ))}
        </Grid>
      )}

      {ready && !examples.brandHasOwnColour && (
        <NoBrandColour data-testid="no-brand-colour">
          These examples are drawn in navy blue. Your brand has no colour of its
          own yet, so this is not your colour:{' '}
          {/*
            A NEW TAB, NOT A LINK ACROSS THE APP. The wizard lives inside a
            pop-up on whatever page the customer was on, and its work -- the
            headlines they have written, the pictures they have swapped -- is
            held in the wizard's own state until the video is rendered. A
            react-router Link unmounts that page, and the pop-up and everything
            in it go with it, with nothing asked and nothing said. Found by the
            review gate, 22 Sep 2026: write a headline on Scenes, come to Style,
            press this, and the headline is gone.
          */}
          <a
            tw="text-indigo-600 font-medium hover:underline cursor-pointer"
            href={brandSettingsHref(examples.brandId)}
            target="_blank"
            rel="noopener noreferrer"
          >
            set your brand colour
          </a>{' '}
          and your video is made in it.
        </NoBrandColour>
      )}
    </Container>
  );
};

export default TemplateStylePicker;
